import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Consumer Mobile App Development",
  "AI Technology Implementation",
  "Digital Subscription Architecture",
  "Compliance / General Inquiry",
];

const URGENCIES = ["Next 30–60 Days", "Upcoming Quarter", "General Scoping"];

const inputClass =
  "w-full px-4 py-3 bg-slate-50 border border-slate-300 rounded text-sm text-slate-900 focus:outline-none focus:border-emerald-600 transition-colors";
const labelClass = "block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2";

export const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    category: CATEGORIES[0],
    urgency: URGENCIES[0],
    message: "",
  });
  const [sending, setSending] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your message has been sent to KAJE. We will respond promptly.");
      setForm({ name: "", email: "", category: CATEGORIES[0], urgency: URGENCIES[0], message: "" });
    } catch (err) {
      toast.error("Message could not be sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 max-w-4xl mx-auto kaje-body" data-testid="contact-section">
      <div className="text-center mb-12">
        <span className="text-xs uppercase font-bold tracking-widest text-emerald-700">
          Get in Touch
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">
          Direct Developer Contact Form
        </h2>
        <p className="text-xs text-slate-500 mt-2 max-w-md mx-auto">
          Connect directly with Justin Kingaby for development inquiries or verification details.
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Email:{" "}
          <a
            href="mailto:kajebiz.store@gmail.com"
            className="text-emerald-700 font-semibold hover:underline"
            data-testid="contact-email-link"
          >
            kajebiz.store@gmail.com
          </a>
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-xl border border-slate-200 p-8 md:p-10 shadow-sm space-y-6"
        data-testid="contact-form"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="contact-name" className={labelClass}>
              Name or Entity *
            </label>
            <input
              type="text"
              id="contact-name"
              required
              value={form.name}
              onChange={update("name")}
              placeholder="e.g. Alex Morgan"
              className={inputClass}
              data-testid="contact-name-input"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className={labelClass}>
              Email Address *
            </label>
            <input
              type="email"
              id="contact-email"
              required
              value={form.email}
              onChange={update("email")}
              placeholder="alex@company.com"
              className={inputClass}
              data-testid="contact-email-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="contact-category" className={labelClass}>
              Project Category
            </label>
            <select
              id="contact-category"
              value={form.category}
              onChange={update("category")}
              className={inputClass}
              data-testid="contact-category-select"
            >
              {CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="contact-urgency" className={labelClass}>
              Target Deployment
            </label>
            <select
              id="contact-urgency"
              value={form.urgency}
              onChange={update("urgency")}
              className={inputClass}
              data-testid="contact-urgency-select"
            >
              {URGENCIES.map((u) => (
                <option key={u}>{u}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="contact-message" className={labelClass}>
            Inquiry / Requirements *
          </label>
          <textarea
            id="contact-message"
            rows="4"
            required
            value={form.message}
            onChange={update("message")}
            placeholder="Detail your mobile application or digital product requirements..."
            className={inputClass}
            data-testid="contact-message-input"
          />
        </div>

        <button
          type="submit"
          disabled={sending}
          className="w-full py-4 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white font-bold text-xs uppercase tracking-wider rounded transition-colors shadow-md"
          data-testid="contact-submit-button"
        >
          {sending ? "Sending..." : "Send Message Direct to Developer"}
        </button>
      </form>
    </section>
  );
};
